/*global _ */
import {fv} from '../../lib/utils'
import {parseDateTime, formatDate, time2Min, getNow} from '../../lib/dates'
import {resetHold} from '../../vuexstore/modules/event-reservation'

export default {
  computed: {
    startOfSelectedDate() {
      const selectedDate = parseDateTime(this.selectedDate)
       if (!selectedDate.isValid) {
         return null
       }
      return selectedDate.startOf('day')
    },
    dayOpenHours() {
      const startOfDay = this.startOfSelectedDate
      if (!startOfDay) {
        return []
      }
      const dayOfWeek = startOfDay.weekday % 7
      return this.allOpenHours[dayOfWeek] || []
    },
    dayStartsAt() {
      const startOfDay = this.startOfSelectedDate
      if (!startOfDay) {
        return null
      }
      return parseDateTime(
        formatDate(startOfDay, 'yyyy-MM-dd') + ' ' +
        (this.dayOpenHours[0] || '00:00')
      )
    },
    dayEndsAt() {
      const startOfDay = this.startOfSelectedDate
      if (!startOfDay) {
        return null
      }
      return parseDateTime(
        formatDate(startOfDay, 'yyyy-MM-dd') + ' ' +
        (this.dayOpenHours[1] || '23:59')
      )
    }
  },
  methods: {
    createTimeslots(data, payload, product) {
      const selectedDate = parseDateTime(this.selectedDate)
      if (!selectedDate.isValid) {
        return []
      }
      const product_id = product.id
      const timeslotDuration = this.timeslotDuration
      const maxParticipants = this.maxParticipantsPerTrack

      const reservedTrackData = _.groupBy(data, 'track')

      const now = getNow()
      const startTime = this.dayStartsAt
      const endTime = this.dayEndsAt.minus({minutes: timeslotDuration || 0})

      let date = startTime.plus({minutes: 0})
      const slots = {}
      while (date <= endTime) {
        // extra 5 minutes buffer added to buffer for hold and finalize process to complete
        const startBufferedTime = date.minus({minutes: this.availabilityBuffer + 5})
        const isPast = this.noMinDate ? false : now > startBufferedTime
        if (!isPast) {
          const dateValue = date.valueOf()
          const datetime = formatDate(date, 'yyyy-MM-dd HH:mm:ss')
          const currentDate = formatDate(date, 'd MMMM yyyy')
          const time = formatDate(date, 'h:mm a')
          const time24 = formatDate(date, 'HH:mm')
          const arriveBy = this.getArriveBy(date)
          const arriveByTime = arriveBy && formatDate(arriveBy, 'h:mm a')
          const tracksState = this.parseTracks(date, timeslotDuration, reservedTrackData)

          slots[time] = {
            product_id,
            datetime,
            dateValue,
            date: currentDate,
            time: time,
            timeAsNumber: time2Min(time),
            time24,
            arriveBy,
            arriveByTime,
            tracksState,
            hold: resetHold({
              start_at: datetime,
              product_id
            }),
            maxParticipants,
            startAt: datetime,
            siblings: [],
            endAt: null,
            duration: 0,
            product_count: 0,
            participants: 0,
            tracks: null,
            selected: false,
            processing: false
          }
        }
        date = date.plus({minutes: timeslotDuration})
      }
      return this.parseSelectableSlots(slots)
    },
    parseSelectableSlots(retrievedSlots) {
      const allSlots = _.values(retrievedSlots)
      // debugLog({SELECTABLE_SLOTS_START: allSlots})
      const duration = this.duration
      const participants = this.participants
      const requiredTimeslotsCount = this.numTimeslots
      const quantity = this.quantity
      const numTracks = this.numTracks
      const orderedTracks = this.availableTracks

      const selectableSlots = _.reduce(allSlots, (slots, slot, index) => {
        const {datetime, tracksState, time} = slot

        slot.product_count = quantity
        slot.participants = participants
        slot.duration = duration
        slot.endAt = parseDateTime(datetime).plus({minutes: duration})
        slot.tracks = null
        slot.siblings = []
        slots[time] = slot

        // find sibling slots (multiple slots combining to form/cover a duration)
        const siblings = _.slice(allSlots, index, index + requiredTimeslotsCount)
        // if number of sibling slots are not same as requiredTimeslotsCount - slot is not visible
        if (siblings.length < requiredTimeslotsCount) {
          return slots
        }
        slot.siblings = _.map(siblings, 'time')

        // logic to decide which lane/tracks are to be booked:
        // Store trackIds from array of tracks having object {<trackId>: <bookable:boolean>}
        const trackIds = _.keys(tracksState).map(v => _.toFinite(v))
        // create array of sibling tracks from sibling slots's tracksState (transposed)
        const siblingTracks = _.unzip(_.map(siblings, s => _.values(s.tracksState)))

        //find which tracks are selectable where each track value must be true (bookable)
        const unorderedSelectableTracks = siblingTracks.map(slotTracks => !_.some(slotTracks, v => !v))

        // order unorderedSelectableTracks based on orderedTracks
        const selectableTracks = _.reduce(orderedTracks, (selectables, track) => {
          const trackIndex = trackIds.indexOf(track)
          if (trackIndex >= 0) {
            selectables.push(unorderedSelectableTracks[trackIndex])
          }
          return selectables
        }, [])

        let selectedIndex
        if (numTracks === 1) {
          selectedIndex = selectableTracks.indexOf(true)
        } else {
          // find the first index from selectable tracks where
          // consecutive (can be 1 for single track or more than one for multitrack booking)
          // selectable tracks are true
          selectedIndex = _.findIndex(selectableTracks, (tracks, trackIndex) => {
            const consecutive = _.slice(selectableTracks, trackIndex, trackIndex + numTracks)
            if (consecutive.length < numTracks) {
              return false
            }
            return !_.some(consecutive, v => !v)
          })
        }

        // consecutive selectable tracks not found - slot will not be visible
        if (selectedIndex < 0) {
          // debugLog({[time]: 'consecutive selectable tracks not found', selectedIndex})
          return slots
        }

        // finally get the track ids to book
        const selectedTracks = _.slice(orderedTracks, selectedIndex, selectedIndex + numTracks)
        // debugLog({time, numTracks, trackIds, siblings, siblingTracks, selectableTracks, selectedIndex, selectedTracks})

        if (selectedTracks.length < numTracks) {
            return slots
        }

        slot.tracks = selectedTracks

        return slots
      }, {})
      // debugLog({SELECTABLE_SLOTS_END: selectableSlots})
      return selectableSlots
    },
    parseTracks(time, duration, reservedTracks) {
      // @todo: use lib/dates:parseDateInterval from v2
      const availableTracks = this.allAvailableTracks
      const interval = time.until(time.plus({minutes: duration}))
      return _.reduce(availableTracks, (tracks, track) => {
        const rTracks = reservedTracks[track]
        if (rTracks && rTracks.length) {
          for(const rTrack of rTracks) {
            const rTrackInterval = parseDateTime(rTrack.start_at)
                .until(parseDateTime(rTrack.end_at))

            if (rTrackInterval.overlaps(interval)) {
              tracks[track] = false
              return tracks
            }
          }
        }
        tracks[track] = true
        return tracks
      }, {})
    },
    getArriveBy(date) {
      const arriveThresholds = this.arrivalThresholds
      const parsedDate = parseDateTime(date)
      const day = parsedDate.weekdayLong.toLowerCase()
      const dateKey = formatDate(parsedDate, 'yyyy-MM-dd')
      const offset = fv(
        arriveThresholds[dateKey],
        arriveThresholds[day],
        arriveThresholds['all'],
        0
      ) || 0

      if (offset) {
        return parsedDate.minus({minutes: offset})
      }
      return null
    }
  }
}
